/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Intelligence overview", description = "Intelligence gives marketers and analysts self-serve analytics inside the same workspace where they build audiences and run campaigns — metrics, charts, dashboards, and experiment measurement, all from your warehouse data.", lastUpdatedDate = new Date(1786924800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Intelligence overview",
        description: "Intelligence gives marketers and analysts self-serve analytics inside the same workspace where they build audiences and run campaigns — metrics, charts, dashboards, and experiment measurement, all from your warehouse data.",
        lastUpdatedDate: "Mon Aug 17 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Access Intelligence",
          "depth": 0,
          "id": "access-intelligence"
        }, {
          "value": "Core features",
          "depth": 0,
          "id": "core-features"
        }, {
          "value": "Define metrics",
          "depth": 1,
          "id": "define-metrics"
        }, {
          "value": "Build charts",
          "depth": 1,
          "id": "build-charts"
        }, {
          "value": "Create dashboards",
          "depth": 1,
          "id": "create-dashboards"
        }, {
          "value": "Measure experiments",
          "depth": 1,
          "id": "measure-experiments"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "For data teams and platform admins",
          "depth": 1,
          "id": "for-data-teams-and-platform-admins"
        }, {
          "value": "For marketers and analysts",
          "depth": 1,
          "id": "for-marketers-and-analysts"
        }, {
          "value": "When to use Intelligence",
          "depth": 0,
          "id": "when-to-use-intelligence"
        }, {
          "value": "Use Intelligence with other Hightouch products",
          "depth": 0,
          "id": "use-intelligence-with-other-hightouch-products"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ol: "ol",
      li: "li",
      a: "a",
      h3: "h3",
      ul: "ul",
      h4: "h4"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Intelligence",
        extraText: ". Intelligence is part of Customer Studio."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Audience"
            }), _jsx(_components.th, {
              children: "How you'll use this article"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Marketing and analysts"
              })
            }), _jsx(_components.td, {
              children: "Understand what Intelligence does and how to use it day-to-day to define metrics, build charts, create dashboards, and measure experiments."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data teams"
              })
            }), _jsx(_components.td, {
              children: "Understand the setup Intelligence requires and how it connects to the rest of the Hightouch platform."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Intelligence gives you self-serve analytics inside your activation workflow. Explore audience composition, measure experiment results, and track campaign performance — all from the same warehouse data you use to build and sync audiences."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Most marketing tools only show analytics for campaigns run on their platform. Business intelligence tools provide broader analysis but sit outside the campaign workflow. Intelligence brings analytics into the same workspace where teams build audiences, measure campaigns, and manage data."
      }), "\n", _jsx(_components.p, {
        children: "Instead of switching between platforms or waiting for reports, you can define shared KPIs, build charts, measure experiments, and track results directly from your warehouse data. Because Intelligence runs on the same connected source and schema as Customer Studio, your charts, audiences, and syncs use the same source data and schema."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "access-intelligence",
        children: "Access Intelligence"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/analytics?from=docs",
            children: _jsx(_components.strong, {
              children: "app.hightouch.com/analytics"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Charts"
          }), ", ", _jsx(_components.strong, {
            children: "Metrics"
          }), ", or ", _jsx(_components.strong, {
            children: "Dashboards"
          }), " to begin."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-features",
        children: "Core features"
      }), "\n", _jsx(_components.h3, {
        id: "define-metrics",
        children: "Define metrics"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/campaign-intelligence/metrics",
          children: "metrics"
        }), " to standardize how your team measures performance. A metric defines a specific calculation — like conversion rate or revenue per user — that you can reuse across charts and dashboards so every team uses the same definition."]
      }), "\n", _jsx(_components.h3, {
        id: "build-charts",
        children: "Build charts"
      }), "\n", _jsx(_components.p, {
        children: "Charts are the building blocks of Intelligence. Choose a chart type based on the question you're trying to answer:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/campaign-intelligence/insights",
              children: "Insights"
            })
          }), " — Analyze audience composition, breakdowns, and performance over time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/campaign-intelligence/funnels",
              children: "Funnels"
            })
          }), " — Identify where users drop off across a sequence of events."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: _jsx(_components.a, {
              href: "/campaign-intelligence/experiments",
              children: "Experiments"
            })
          }), " — Compare results between randomized test and control groups."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/campaign-intelligence/charts",
          children: "Charts overview"
        }), " for details on configuring, saving, and updating charts. After building a chart, save it so you can reference it later or add it to a dashboard."]
      }), "\n", _jsx(_components.h3, {
        id: "create-dashboards",
        children: "Create dashboards"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/campaign-intelligence/dashboards",
          children: "Dashboards"
        }), " combine saved charts into a single monitoring view. Use them to track ongoing campaign performance, compare audience segments, or review experiment results over time."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "All charts in a dashboard must share the same parent model. Plan your chart\nstrategy around this constraint when building dashboards."
        })
      }), "\n", _jsx(_components.h3, {
        id: "measure-experiments",
        children: "Measure experiments"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're running an A/B test or holdout group, use Experiments to measure whether a treatment worked. Create ", _jsx(_components.a, {
          href: "/customer-studio/experiments",
          children: "audience experiments"
        }), " in Customer Studio to randomly divide an audience into test and control groups, then measure results with ", _jsx(_components.a, {
          href: "/campaign-intelligence/experiments",
          children: "experiment charts"
        }), " in Intelligence."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Experiments require several prerequisites before results appear: audience\nsnapshots must be enabled, split groups must be created, at least one sync\nmust run after split creation, and post-snapshot event data must exist for\nmeasurement."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "Intelligence shares the same schema and source configuration as Customer Studio. If your workspace already has a schema configured, marketers can start using Intelligence immediately."
      }), "\n", _jsx(_components.h3, {
        id: "for-data-teams-and-platform-admins",
        children: "For data teams and platform admins"
      }), "\n", _jsx(_components.p, {
        children: "Complete these steps if your workspace doesn't already have a schema configured. This setup is typically done once per workspace."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Step"
            }), _jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Why"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "1"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/sources/overview",
                children: "Connect a source"
              })
            }), _jsx(_components.td, {
              children: "Intelligence queries warehouse data directly — it needs at least one connected source."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "2"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "Define your data schema"
              })
            }), _jsx(_components.td, {
              children: "The schema defines the parent model, events, and related models that power metrics and charts."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "3"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/destinations/overview",
                children: "Add destinations (optional)"
              })
            }), _jsx(_components.td, {
              children: "Connect destinations if your team will move from analysis to activation, such as syncing refined audiences after reviewing chart results."
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "If your workspace already has a Customer Studio schema configured, skip\ndirectly to the marketer workflow below."
        })
      }), "\n", _jsx(_components.h3, {
        id: "for-marketers-and-analysts",
        children: "For marketers and analysts"
      }), "\n", _jsxs(_components.p, {
        children: ["Once the schema is set up, the core workflow is: define a metric, build a chart, save it, and optionally add it to a dashboard. Start by ", _jsx(_components.a, {
          href: "/campaign-intelligence/metrics",
          children: "creating a metric"
        }), " for a core KPI your team tracks, then build an ", _jsx(_components.a, {
          href: "/campaign-intelligence/insights",
          children: "Insights chart"
        }), " to visualize it. From there, explore ", _jsx(_components.a, {
          href: "/campaign-intelligence/funnels",
          children: "funnels"
        }), " for drop-off analysis, ", _jsx(_components.a, {
          href: "/campaign-intelligence/experiments",
          children: "experiment charts"
        }), " for test measurement, and ", _jsx(_components.a, {
          href: "/campaign-intelligence/dashboards",
          children: "dashboards"
        }), " to organize everything into a monitoring view."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "when-to-use-intelligence",
        children: "When to use Intelligence"
      }), "\n", _jsx(_components.p, {
        children: "Intelligence is the right tool when you need to analyze, measure, or visualize data that's already in your warehouse. For activation and campaign coordination, use Customer Studio or AI Decisioning instead."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended approach"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Define shared KPIs and reuse them across charts and dashboards"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Intelligence"
              }), " ", _jsx(_components.a, {
                href: "/campaign-intelligence/metrics",
                children: "Metrics"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Analyze audience composition, breakdowns, or trends over time"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Intelligence"
              }), " ", _jsx(_components.a, {
                href: "/campaign-intelligence/insights",
                children: "Insights"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Identify where users drop off in a conversion sequence"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Intelligence"
              }), " ", _jsx(_components.a, {
                href: "/campaign-intelligence/funnels",
                children: "Funnels"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Measure A/B test or holdout group results"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Intelligence"
              }), " ", _jsx(_components.a, {
                href: "/campaign-intelligence/experiments",
                children: "Experiments"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Build audiences and sync them to destinations"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Automate which message, channel, and timing each user receives"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/overview",
                children: "AI Decisioning"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-intelligence-with-other-hightouch-products",
        children: "Use Intelligence with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Intelligence measures how your audiences and campaigns perform. It reads from the same warehouse data that powers audiences and syncs, so your analytics and activation stay aligned."
      }), "\n", _jsx(_components.h4, {
        id: "audiences-experiments-and-warehouse-data-intelligence-reads",
        children: "Audiences, experiments, and warehouse data Intelligence reads"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Audiences and traits from ", _jsx(_components.a, {
              href: "/customer-studio/overview",
              children: "Customer Studio"
            })]
          }), " — Intelligence analyzes the audiences you build, using traits and event data already defined in your schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Experiment groups from ", _jsx(_components.a, {
              href: "/customer-studio/experiments",
              children: "Experiments"
            })]
          }), " — When you create randomized test and control groups in Customer Studio, Intelligence provides the charts to measure results."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsxs(_components.strong, {
            children: ["Warehouse data from your ", _jsx(_components.a, {
              href: "/sources/overview",
              children: "connected source"
            })]
          }), " — Metrics and charts query your warehouse directly, so any data available to Hightouch is available in Intelligence."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "refine-audiences-and-share-dashboards",
        children: "Refine audiences and share dashboards"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insights that refine audiences"
          }), " — After reviewing chart results or funnel drop-offs, marketers can adjust audience logic or create new segments directly in Customer Studio."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Dashboards for stakeholders"
          }), " — Combine charts into shareable dashboards so teams can monitor campaign performance and customer trends in one place."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Because Intelligence and the ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents"
        }), " workspace read the same warehouse data, you can also ask an agent to analyze results in natural language and save any analysis as a scheduled report."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Access to Intelligence is governed by Customer Studio schema and model permissions, plus the analytics entitlement for your workspace. There is no separate Intelligence role or grant tab. See ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "Roles"
        }), " for Customer Studio grants."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
